import { useRecoilState, useRecoilValue } from 'recoil';
import * as Atom from '../recoil/index';
import useNav from '../hooks/useNav';
import { useEffect, useState } from 'react';
import useBeacon, { ClkBtnSubEventNm } from '../hooks/useBeacon';
import { PAGE_NM, PLACEMENT_TYPE } from '../types/types';
import { beaconSelector } from '../recoil/selector';
import { useParams } from 'react-router-dom';
import {
  HOME_BANNER,
  HOME_BANNER2,
  HOME_BANNER_MTG,
  HOME_DEFAULT_SIMPLE_LIST,
} from '../staticData/Banner/HOME_BANNER';
import { useBanner } from '../hooks/useBanner';
import { useCardList } from '../hooks/useCardList';
import { useTerms } from '../hooks/useTerms';
import useRedirect from '../hooks/useRedirect';
import { useBaseParam } from '../hooks/useBaseParam';
import { fetchCurrentPromotion } from '../../infra/api/fetchCurrentPromotion';
import eventTimer from '../helper/eventTimer';

const useLanding = (pageNm: string) => {
  const { navigate, navigateCreditOrMyDataByAppversion, canUseCredit } =
    useNav();
  const { media } = useParams();
  const { isRedirect } = useRedirect();

  const [landingInfo] = useRecoilState(Atom.loanList);
  const [baseContents] = useRecoilState(Atom.baseContents);

  const [isFloating, setIsFloating] = useState<boolean>(true);
  const [isLazy, setIsLazy] = useState<boolean>(true);

  const navigatePartners = () => {
    navigate('/partners', { state: { ref_page: pageNm } });
  };
  const navigateBannerToPage = (idx: number, dest?: string) => {
    const banner = ['/rateCalc'];
    navigate(dest ? dest : banner[idx]);
  };

  const navigateTo = ({ destination, refPage, refBtnNm }: any) => {
    navigate(destination, {
      state: {
        refPage: refPage ?? '',
        refSource: refBtnNm ?? '',
      },
    });
  };

  const beaconData = useRecoilValue(beaconSelector);
  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
    pageName: pageNm,
  });

  useEffect(() => {
    beacon.sendViewPage();
  }, []);

  const sendBeaconClkBtnBnkList = (btnNm: string) => {
    beacon.sendClkBtnBnkList({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnLoanApp = (btnNm: string) => {
    beacon.sendClkBtnLoanApp({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnAgree = (btnNm: string) => {
    beacon.sendClkBtnAgree({
      properties: { btnNm },
    });
  };

  const sendBeaconClkBtnPrmEvnt = (btnNm: string) => {
    beacon.sendClkBtnPrmEvnt({
      properties: { btnNm },
    });
  };

  const sendBeaconClkBtnNotiSchAlrt = (btnNm: string) => {
    beacon.sendClkBtnNotiSchAlrt({
      properties: { btnNm },
    });
  };

  const sendBeaconClkBtnIntrstCalc = (btnNm: string) => {
    beacon.sendClkBtnIntrstCalc({
      properties: { btnNm },
    });
  };

  const sendBeaconClkBtnLoanPrg = (btnNm: string) => {
    beacon.sendClkBtnLoanPrg({
      properties: { btnNm },
    });
  };

  const sendBeaconClkBtnCardFinPrd = (btnNm: string) => {
    beacon.sendClkBtnCardFinPrd({
      properties: { btnNm },
    });
  };

  const sendBeaconClkBtnCardInstrstCalc = (btnNm: string) => {
    beacon.sendClkBtnCardInstrstCalc({
      properties: { btnNm },
    });
  };

  const sendBeaconClkBtnCardNotiSchAlrt = (btnNm: string) => {
    beacon.sendClkBtnCardNotiSchAlrt({
      properties: { btnNm },
    });
  };

  const sendBeaconClkBnrBnrPromotion = (btnNm: string) => {
    beacon.sendClkBnrBnrPromotion({
      properties: {
        btnNm,
        promotion_type: 'is',
        promotion_str_dt: '2023-04-17',
        promotion_end_dt: '2023-05-31',
        promotion_seq: '2',
      },
    });
  };

  const sendBeaconClkBtn = (subEventNm: string, btnNm: string) => {
    beacon.sendClkBtn({
      events: { subEventName: subEventNm },
      properties: { btnNm: btnNm },
    });
  };

  const navigateCreditResult = () => {
    navigate('/result');
  };

  const navigateCreditMain = () => {
    navigate('/main');
  };

  const navigateNoti = () => {
    navigate('/notiPreReg');
  };

  const navigateRateCalc = () => {
    navigate('/rateCalc');
  };
  const navigateResult = () => {
    navigate('/mtg/result');
  };
  const navigateStatus = (isMtg?: boolean) => {
    navigate(isMtg ? '/mtg/result-status' : '/result-status', {
      state: landingInfo,
    });
  };

  const getCurrentPromotion = useBaseParam(fetchCurrentPromotion, {
    mediaCd: beaconData.mediaCd,
  });

  const getIsMaintenance = () => {
    if (media === 'lgu') {
      return eventTimer(
        process.env.REACT_APP_LGU_MAINTENANCE_START || '',
        process.env.REACT_APP_LGU_MAINTENANCE_END || '',
        new Date().toISOString(),
      );
    } else return false;
  };

  const [creditChipText, setCreditChipText] = useState<String>('');
  const [mtgChipText, setMtgChipText] = useState<String>('');
  const [creditSubTitle, setCreditSubTitle] = useState<String>('');
  const [mtgSubTitle, setMtgSubTitle] = useState<String>('');

  useEffect(() => {
    getCurrentPromotion().then((response) => {
      if (
        response &&
        response.data &&
        response.data.isSucceed &&
        response.data.result
      ) {
        const { linkType1, chip1, subTitle1, linkType2, chip2, subTitle2 } =
          response.data.result;

        const creditChip =
          (linkType1 === 'CREDIT_LOAN'
            ? chip1
            : linkType2 === 'CREDIT_LOAN'
            ? chip2
            : '') ?? '';
        const mtgChip =
          (linkType1 === 'HOUSE_LOAN'
            ? chip1
            : linkType2 === 'HOUSE_LOAN'
            ? chip2
            : '') ?? '';

        const creditSub =
          (linkType1 === 'CREDIT_LOAN'
            ? subTitle1
            : linkType2 === 'CREDIT_LOAN'
            ? subTitle2
            : '') ?? '';
        const mtgSub =
          (linkType1 === 'HOUSE_LOAN'
            ? subTitle1
            : linkType2 === 'HOUSE_LOAN'
            ? subTitle2
            : '') ?? '';
        setCreditChipText(creditChip);
        setMtgChipText(mtgChip);
        setCreditSubTitle(creditSub);
        setMtgSubTitle(mtgSub);
      }
    });

    setTimeout(() => {
      setIsLazy(false);
    }, 2000);
  }, []);

  const hasLoanStatus =
    landingInfo?.preRequestInfoChk &&
    landingInfo?.dayAgo < 30 &&
    (landingInfo?.practiceProductItemList?.length > 0 ||
      landingInfo?.applyingProductList?.length > 0 ||
      landingInfo?.practiceProductList?.length > 0 ||
      landingInfo?.cancelProductList?.length > 0 ||
      landingInfo?.searchedPreProductList?.length > 0 ||
      landingInfo?.rejectProductItemList?.length > 0);

  const isAvailableToday =
    !!landingInfo.toDayRemainTime && landingInfo.toDayRemainTime !== '00:00:00';

  const loanStatusCount =
    landingInfo?.applyingProductList?.length +
    (isAvailableToday ? landingInfo?.searchedPreProductList?.length : 0);

  // banner
  const {
    isLoading: isLoadingBannerList,
    bannerList,
    hasBannerList,
  } = useBanner(
    media || '',
    PLACEMENT_TYPE.loan_main_top,
    baseContents.serverTime,
  );

  // cardList
  const { cardList } = useCardList(media || '');

  // terms
  const termsProps =
    pageNm === PAGE_NM.MTG_HOME
      ? { EVENT_STATE: 'mtg', ref_page: pageNm }
      : pageNm;
  const terms = useTerms(termsProps);

  const getSimpleList = () => {
    if (pageNm === PAGE_NM.HOME) return HOME_DEFAULT_SIMPLE_LIST;
    if (pageNm === PAGE_NM.MTG_HOME) return HOME_BANNER_MTG;
    if (hasLoanStatus) {
      return HOME_BANNER2;
    } else {
      return HOME_BANNER;
    }
  };

  const getSimpleListWithChip = () => {
    return getSimpleList().map((item: any) => {
      if (item && item.beacon) {
        if (item.beacon.subEventName === ClkBtnSubEventNm.GoMtgMain) {
          const chip = mtgChipText
            ? [
                {
                  color: 'tertiary',
                  text: mtgChipText,
                },
              ]
            : [];
          return {
            ...item,
            chip,
            subheaderText: mtgSubTitle,
          };
        } else if (item.beacon.subEventName === ClkBtnSubEventNm.GoCreMain) {
          const chip = creditChipText
            ? [
                {
                  color: 'tertiary',
                  text: creditChipText,
                },
              ]
            : [];
          return {
            ...item,
            chip,
            subheaderText: creditSubTitle,
          };
        }
      }
      return item;
    });
  };

  const [, setMortgageInfo] = useRecoilState(Atom.mortgageInfo);
  const resetMortgageInfo = () => {
    setMortgageInfo(Atom.createDefaultMortgageInfo());
  };

  return {
    isRedirect,
    terms,
    hasBannerList,
    bannerList: bannerList.map((item: any) => ({
      ...item,
      // todo send beacon - 기획 정의 x
      beacon: {
        click: () =>
          item.bannerSeq === 0
            ? sendBeaconClkBnrBnrPromotion('프로모션 배너')
            : () => {},
        view: () => {},
      },
    })),
    navigateCreditResult,
    navigateCreditMain,
    navigateTo,
    canUseCredit,
    navigateCreditOrMyDataByAppversion,
    navigatePartners,
    navigateBannerToPage,
    navigateStatus,
    navigateResult,
    isLazy,
    isTodayRequested:
      landingInfo?.dayAgo === 0 && landingInfo?.preRequestInfoChk === true,

    sendBeaconClkBtn,
    sendBeaconClkBtnBnkList,
    sendBeaconClkBtnLoanApp,
    sendBeaconClkBtnNotiSchAlrt,
    sendBeaconClkBtnIntrstCalc,
    sendBeaconClkBtnLoanPrg,
    sendBeaconClkBtnCardFinPrd,
    sendBeaconClkBtnCardInstrstCalc,
    sendBeaconClkBtnCardNotiSchAlrt,
    sendBeaconClkBnrBnrPromotion,

    creditChipText,
    mtgChipText,

    pageName: pageNm,
    navigateNoti,
    navigateRateCalc,
    sendBeaconClkBtnPrmEvnt,

    isFloating,
    setIsFloating,
    hasLoanStatus,
    loanStatusCount,

    resetMortgageInfo,

    simpleList: getSimpleListWithChip(),
    cardList: cardList.map((item) => ({
      ...item,
      beacon: () => sendBeaconClkBtn(item.beacon.subEventNm, item.primaryText),
    })),

    media,

    isMaintenance: getIsMaintenance(),
  };
};

export default useLanding;
